import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { setLoading } from "./LoadingSlice";
import Unauthorized from "../Admin/Unauthorized";
import { toast } from "react-toastify";
import BaseUrl from "../../BaseUrl";
import { jwtDecode } from 'jwt-decode';

// --- API URLs and Headers ---
const headers = Unauthorized.Actionsauthorized();
const consulturl = BaseUrl.consultationurl;
const medicinemasterurl = BaseUrl.pharmacyurl;
const specialtyInfourl = BaseUrl.templatesurl;
const labsurl = BaseUrl.labsurl;

// --- Group ID for imaging services (adjust according to your API) ---
const IMAGING_GROUP_ID = 13; // services with groupId === 13 will be treated as imaging

// --- Initial State ---
const initialState = {
    activeComponent: 'vitalSigns',
    consultationType: '',
    consultationDetails: {
        ChiefComplaint: "",
        HPI: "",
        Comorbidities: "", // Will be mapped to Comorbidity for API
        SystemicExamination: "",
        FinalDiagnosis: "",
        FinalDiagnosisNotes: "",
        FinalDiagnosisId: null,
        Weight: null,
        Height: null,
        BMI: null,
        BodySurfaceArea: null,
        prescribingDoctor: '',
    },
    labTestDescription: '',
    imagingDescription: '',
    patientDetails: { name: '', age: '', gender: '', dob: '', mrn: '' },
    consultationId: '',
    medicineDetails: [],
    labTestDetails: {
        Services: "",
        Packages: ""
    },
    selectedPackages: [],
    selectedServices: [],
    patientServiceList: [], // Store full PatientServiceList from API
    imagingServicesMaster: [],
    selectedImagingServices: [],
    serviceGroups: [],
    labTestServices: [],
    packagesMaster: [],
    specialtyInfo: [],
    medicinemaster: [],
    specialtyInfomaster: [],
    diagnosisMaster: [],
    preparedDiagnoses: [],
    additionalDiagnoses: [],
    department: { deptId: "", deptname: "" },
    selectedTemplates: [],
    prescribedMedicines: [],
    images: [],
    consultationData: {},
    reviewComplete: {},
    pastConsultations: [],
    patientConsultations: [],
    laafiRecommendations: '',
    aiSuggestion: null,
    aiError: null,
    vitalSigns: {
        bpSystolic: '',
        bpDiastolic: '',
        heartRate: '',
        respiratoryRate: '',
        temperature: '',
        oxygenSaturation: '',
        weight: null,
        height: null,
        bmi: null,
        bodySurfaceArea: null
    },
    referralDoctorId: null,
    referralNotes: '',
    referralStatus: 'Pending',
    selectedReferralDoctor: null,
};

// --- Async Thunks ---
export const fetchConsultationById = createAsyncThunk(
    "outpatient/fetchConsultationById",
    async ({ consultationId, consultationType }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(
                `${consulturl}Consultations/GetConsultationById?ConsultationId=${consultationId}&Type=${consultationType}`,
                { headers }
            );
            dispatch(setLoading(false));
            const departmentId = response.data.DepartmentId;
            
            const token = localStorage.getItem('accessToken');
            let isOptometrist = false;
            if (token) {
                try {
                    const decoded = jwtDecode(token);
                    const roleClaim = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
                    let roles = [];
                    if (Array.isArray(roleClaim)) {
                        roles = roleClaim;
                    } else if (typeof roleClaim === 'string') {
                        roles = [roleClaim];
                    }
                    isOptometrist = roles.includes('Optometrist');
                } catch (e) {
                    console.error('Error decoding token:', e);
                }
            }
            
            const deptIdToUse = isOptometrist ? 21 : departmentId;
            
            if (deptIdToUse) {
                dispatch(fetchSpecialtyInfoMaster(deptIdToUse));
            }
            
            try {
                await dispatch(fetchPatientDiagnoses(consultationId));
            } catch (e) {
                console.log("No diagnoses found for this consultation");
            }
            
            await dispatch(fetchMedicineMaster());
            
            console.log(response.data);
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error(error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchOptometristTemplates = createAsyncThunk(
    "outpatient/fetchOptometristTemplates",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(
                `${specialtyInfourl}Templates/FindByDepartmentId?departmentId=21`,
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch optometrist templates:", error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchConsultationImages = createAsyncThunk(
    "outpatient/fetchConsultationImages",
    async (consultationId, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(
                `${consulturl}a7eceaf2-d766-4c55-a8bd-ccb6c1e0dbec/${consultationId}`,
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch consultation images:", error);
            if (error.response && error.response.status !== 404) {
                toast.error("Failed to load patient images.");
            }
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchMedicineMaster = createAsyncThunk(
    "outpatient/fetchMedicineMaster",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.get(
                `${medicinemasterurl}MedicineMasters`,
                { headers: { 'accept': 'text/plain' } }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch medicine master:", error);
            toast.error("Failed to load medicine list.");
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const addNewMedicineToMaster = createAsyncThunk(
    "outpatient/addNewMedicineToMaster",
    async (medicineData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.post(
                `${medicinemasterurl}MedicineMasters`,
                medicineData,
                { headers: { 'Content-Type': 'application/json', 'accept': 'text/plain' } }
            );
            dispatch(setLoading(false));
            toast.success("Medicine added successfully!");
            await dispatch(fetchMedicineMaster());
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to add medicine:", error);
            toast.error("Failed to add medicine. Please try again.");
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchSpecialtyInfoMaster = createAsyncThunk(
    "outpatient/fetchSpecialtyInfoMaster",
    async (departmentId, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(
                `${specialtyInfourl}Templates/FindByDepartmentId?departmentId=${departmentId}`,
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch specialty info master:", error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchAllDiagnosis = createAsyncThunk(
    "outpatient/fetchAllDiagnosis",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.post(
                `${consulturl}Consultations/GetCompleteDiagnosesList`,
                {},
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch all diagnosis:", error);
            toast.error("Failed to load diagnosis list.");
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const registerNewDiagnosis = createAsyncThunk(
    "outpatient/registerNewDiagnosis",
    async (newDiagnosisData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const data = {
                Name: newDiagnosisData.name,
                Desc: newDiagnosisData.description || "User defined diagnosis",
            };
            const response = await axios.post(
                `${consulturl}Consultations/RegisterNewDiagnosis`,
                data,
                { headers }
            );
            const result = response.data;
            if (result) {
                toast.success(result.Message || `${newDiagnosisData.name} added to master list.`);
                await dispatch(fetchAllDiagnosis());
            }
            return result;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to register new diagnosis:", error);
            toast.error("Failed to register new diagnosis. Please try again.");
            return rejectWithValue(error.response ? error.response.data : error.message);
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const fetchPatientDiagnoses = createAsyncThunk(
    "outpatient/fetchPatientDiagnoses",
    async (consultationId, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(
                `${consulturl}Consultations/GetPatientDiagnoses?ConsultationId=${consultationId}`,
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch patient diagnoses:", error);
            if (error.response && error.response.status !== 404) {
                toast.error("Failed to fetch patient diagnoses.");
            }
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const savePatientDiagnoses = createAsyncThunk(
    "outpatient/savePatientDiagnoses",
    async ({ consultationId, diagnoses }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.post(
                `${consulturl}Consultations/SavePatientDiagnoses?ConsultationId=${consultationId}`,
                diagnoses,
                { headers }
            );
            dispatch(setLoading(false));
            toast.success("Diagnoses saved successfully!");
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to save patient diagnoses:", error);
            toast.error("Failed to save diagnoses. Please try again.");
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchServiceGroups = createAsyncThunk(
    "outpatient/fetchServiceGroups",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(`${labsurl}ServiceGroups`, { headers });
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch service groups:", error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchLabTestServices = createAsyncThunk(
    "outpatient/fetchLabTestServices",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.get(`${labsurl}LabTestServices`);
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch lab test services:", error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchLabTestServicesByGroup = createAsyncThunk(
    "outpatient/fetchLabTestServicesByGroup",
    async (groupId, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(
                `${labsurl}LabTests/GetLabTestServicesByGroupId?ServiceGroupId=${groupId}`,
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch lab test services:", error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchPackages = createAsyncThunk(
    "outpatient/fetchPackages",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await axios.get(`${labsurl}Packages`);
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch packages:", error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchPackageServicesById = createAsyncThunk(
    "outpatient/fetchPackageServicesById",
    async (packageId, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(
                `${labsurl}LabTests/GetPackageServicesByPackageId?PackageId=${packageId}`,
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            console.error("Failed to fetch package services:", error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const analyzeImage = createAsyncThunk(
    "outpatient/analyzeImage",
    async ({ imageFile, consultationId, consultationType }, { dispatch }) => {
        try {
            dispatch(setLoading(true));
            const formData = new FormData();
            formData.append("Image", imageFile);
            const response = await axios.post(
                `${consulturl}Consultations/ChatLaafiRecommendationOnImage?ConsultationId=${consultationId}&ConsultationType=${consultationType}`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            toast.success(`${response.data.Message}`);
            return response.data.Message;
        } catch (error) {
            toast.error("AI analysis failed. Please try again.");
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const aiDiagnosisPost = createAsyncThunk(
    "outpatient/aiDiagnosisPost",
    async (_, { getState, dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const { consultationDetails, consultationId } = getState().outpatient;
            const headers = Unauthorized.Actionsauthorized();
            const data = {
                HPI: consultationDetails.HPI,
                ChiefComplaint: consultationDetails.ChiefComplaint,
                SystemicExamination: consultationDetails.SystemicExamination
            };
            const response = await axios.post(
                `${consulturl}Consultations/GetLaafiRecommendation?ConsultationId=${consultationId}`,
                data,
                { headers }
            );
            const recommendation = response.data.Recommendation || response.data.Message || "";
            toast.success(response.data.Message || "AI Diagnosis Complete");
            return recommendation;
        } catch (error) {
            console.error("AI Diagnosis failed:", error);
            toast.error("AI Diagnosis failed. Please try again.");
            return rejectWithValue(error.response ? error.response.data : error.message);
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const updateConsultationStatus = createAsyncThunk(
    "outpatient/updateConsultationStatus",
    async ({ consultationId, consultationType, formattedData }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.patch(
                `${consulturl}Consultations/DoctorConsultationUpdates?ConsultationId=${consultationId}&ConsultationType=${consultationType}`,
                formattedData,
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const getPatientConsultations = createAsyncThunk(
    'outpatient/getPatientConsultations',
    async (mrn, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(
                `${consulturl}Consultations/GetPatientConsultations?MRN=${mrn}`,
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchPatientConsultations = createAsyncThunk(
    'outpatient/fetchPatientConsultations',
    async (mrn, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.get(
                `${consulturl}Consultations/GetPatientConsultations?MRN=${mrn}`,
                { headers }
            );
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const fetchLaafiRecommendations = createAsyncThunk(
    'outpatient/fetchLaafiRecommendations',
    async (data, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const headers = Unauthorized.Actionsauthorized();
            const response = await axios.post(`${consulturl}Consultations/GetLaafiMedicineRecommendation`, data, { headers });
            dispatch(setLoading(false));
            return response.data;
        } catch (error) {
            dispatch(setLoading(false));
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// --- Slice Definition ---
const outpatientSlice = createSlice({
    name: 'outpatient',
    initialState,
    reducers: {
        setActiveComponent: (state, action) => {
            state.activeComponent = action.payload;
        },
        setConsultationType: (state, action) => {
            state.consultationType = action.payload;
        },
        updateConsultationDetails: (state, action) => {
            state.consultationDetails = { ...state.consultationDetails, ...action.payload };
        },
        updatePatientDetails: (state, action) => {
            state.patientDetails = { ...state.patientDetails, ...action.payload };
        },
        updateMedicineDetails: (state, action) => {
            if (Array.isArray(action.payload)) {
                state.prescribedMedicines = action.payload;
            } else {
                const newMedicine = {
                    ...action.payload,
                    Dosage: action.payload.Dosage || '',
                    PrescriptionForm: action.payload.PrescriptionForm || ''
                };
                state.prescribedMedicines = [...state.prescribedMedicines, newMedicine];
            }
        },
        removeMedicine: (state, action) => {
            state.prescribedMedicines = state.prescribedMedicines.filter((_, index) => index !== action.payload);
        },
        clearPrescribedMedicines: (state) => {
            state.prescribedMedicines = [];
        },
        updateLabTestDetails: (state, action) => {
            state.labTestDetails = { ...state.labTestDetails, ...action.payload };
        },
        setLabTestServices: (state, action) => {
            state.labTestServices = action.payload;
        },
        clearLabTestServices: (state) => {
            state.labTestServices = [];
        },
        addSelectedPackage: (state, action) => {
            const newPackage = action.payload;
            if (newPackage.id && !state.selectedPackages.some(pkg => pkg.id === newPackage.id)) {
                state.selectedPackages.push(newPackage);
                const ids = state.selectedPackages.map(pkg => pkg.id).filter(id => id).join(',');
                state.labTestDetails.Packages = ids;
            }
        },
        removeSelectedPackage: (state, action) => {
            const packageId = action.payload;
            state.selectedPackages = state.selectedPackages.filter(pkg => pkg.id !== packageId);
            const ids = state.selectedPackages.map(pkg => pkg.id).filter(id => id).join(',');
            state.labTestDetails.Packages = ids;
        },
        addSelectedService: (state, action) => {
            const newService = action.payload;
            if (newService.id && !state.selectedServices.some(service => service.id === newService.id)) {
                state.selectedServices.push(newService);
                const ids = state.selectedServices.map(service => service.id).filter(id => id).join(',');
                state.labTestDetails.Services = ids;
            }
        },
        removeSelectedService: (state, action) => {
            const serviceId = action.payload;
            state.selectedServices = state.selectedServices.filter(service => service.id !== serviceId);
            const ids = state.selectedServices.map(service => service.id).filter(id => id).join(',');
            state.labTestDetails.Services = ids;
        },
        addSelectedImagingService: (state, action) => {
            const newService = action.payload;
            if (newService.id && !state.selectedImagingServices.some(s => s.id === newService.id)) {
                state.selectedImagingServices.push(newService);
            }
        },
        removeSelectedImagingService: (state, action) => {
            const serviceId = action.payload;
            state.selectedImagingServices = state.selectedImagingServices.filter(s => s.id !== serviceId);
        },
        clearSelectedImagingServices: (state) => {
            state.selectedImagingServices = [];
        },
        setSelectedTemplates: (state, action) => {
            state.selectedTemplates = action.payload;
        },
        addImages: (state, action) => {
            state.images = [...state.images, ...action.payload];
        },
        removeImage: (state, action) => {
            state.images = state.images.filter((_, index) => index !== action.payload);
        },
        clearImages: (state) => {
            state.images = [];
        },
        updateReviewComplete: (state, action) => {
            state.reviewComplete = { ...state.reviewComplete, ...action.payload };
        },
        updateReferralDoctor: (state, action) => {
            state.referralDoctorId = action.payload;
        },
        updateReferralNotes: (state, action) => {
            state.referralNotes = action.payload;
        },
        updateReferralStatus: (state, action) => {
            state.referralStatus = action.payload;
        },
        updateSelectedReferralDoctor: (state, action) => {
            state.selectedReferralDoctor = action.payload;
        },
        addSelectedTemplate: (state, action) => {
            state.selectedTemplates = [...state.selectedTemplates, action.payload];
            state.specialtyInfo.push({
                TemplateId: action.payload.Id,
                SpecialtyInfos: action.payload.TemplateData.map((field) => ({
                    FieldName: field.FieldName,
                    FieldType: field.FieldType,
                    Value: '',
                })),
            });
        },
        removeSelectedTemplate: (state, action) => {
            state.selectedTemplates = state.selectedTemplates.filter(template => template.Id !== action.payload);
            state.specialtyInfo = state.specialtyInfo.filter(info => info.TemplateId !== action.payload);
        },
        updateMedicineAtIndex: (state, action) => {
            const { index, medicine } = action.payload;
            if (index >= 0 && index < state.prescribedMedicines.length) {
                state.prescribedMedicines[index] = {
                    ...medicine,
                    Dosage: medicine.Dosage || '',
                    PrescriptionForm: medicine.PrescriptionForm || ''
                };
            }
        },
        updateSpecialtyInfo: (state, action) => {
            const { templateId, fieldName, value } = action.payload;
            const templateIndex = state.specialtyInfo.findIndex(info => info.TemplateId === templateId);
            if (templateIndex !== -1) {
                const fieldIndex = state.specialtyInfo[templateIndex].SpecialtyInfos.findIndex(
                    field => field.FieldName === fieldName
                );
                if (fieldIndex !== -1) {
                    state.specialtyInfo[templateIndex].SpecialtyInfos[fieldIndex].Value = value;
                }
            }
        },
        setAiSuggestion: (state, action) => {
            state.aiSuggestion = action.payload;
        },
        clearAiSuggestion: (state) => {
            state.aiSuggestion = null;
        },
        updateWeight: (state, action) => {
            state.weight = action.payload;
            if (state.weight && state.height) {
                const heightM = state.height / 100;
                state.bmi = (state.weight / (heightM * heightM)).toFixed(1);
                state.bodySurfaceArea = Math.sqrt((state.height * state.weight) / 3600).toFixed(2);
            } else {
                state.bmi = null;
                state.bodySurfaceArea = null;
            }
        },
        updateHeight: (state, action) => {
            state.height = action.payload;
            if (state.weight && state.height) {
                const heightM = state.height / 100;
                state.bmi = (state.weight / (heightM * heightM)).toFixed(1);
                state.bodySurfaceArea = Math.sqrt((state.height * state.weight) / 3600).toFixed(2);
            } else {
                state.bmi = null;
                state.bodySurfaceArea = null;
            }
        },
        updateMainDiagnosis: (state, action) => {
            const { diagnosisName, diagnosisId, notes } = action.payload;
            state.consultationDetails.FinalDiagnosis = diagnosisName;
            state.consultationDetails.FinalDiagnosisId = diagnosisId;
            if (notes !== undefined) {
                state.consultationDetails.FinalDiagnosisNotes = notes;
            }
        },
        addAdditionalDiagnosis: (state) => {
            const newId = state.additionalDiagnoses.length > 0
                ? Math.max(...state.additionalDiagnoses.map(d => d.id)) + 1
                : 1;
            state.additionalDiagnoses.push({
                id: newId,
                value: '',
                notes: '',
                diagnosisId: null
            });
        },
        updateVitalSigns: (state, action) => {
            state.vitalSigns = { ...state.vitalSigns, ...action.payload };
            
            const weight = state.vitalSigns.weight;
            const height = state.vitalSigns.height;
            if (weight && height && weight > 0 && height > 0) {
                const heightM = height / 100;
                state.vitalSigns.bmi = (weight / (heightM * heightM)).toFixed(1);
                state.vitalSigns.bodySurfaceArea = Math.sqrt((height * weight) / 3600).toFixed(2);
            } else {
                state.vitalSigns.bmi = null;
                state.vitalSigns.bodySurfaceArea = null;
            }
        },
        updateLabTestDescription: (state, action) => {
            state.labTestDescription = action.payload;
        },
        updateImagingDescription: (state, action) => {
            state.imagingDescription = action.payload;
        },
        updateAdditionalDiagnosis: (state, action) => {
            const { id, value, notes, diagnosisId } = action.payload;
            const diagnosisIndex = state.additionalDiagnoses.findIndex(diag => diag.id === id);
            if (diagnosisIndex !== -1) {
                if (value !== undefined) state.additionalDiagnoses[diagnosisIndex].value = value;
                if (notes !== undefined) state.additionalDiagnoses[diagnosisIndex].notes = notes;
                if (diagnosisId !== undefined) state.additionalDiagnoses[diagnosisIndex].diagnosisId = diagnosisId;
            }
        },
        removeAdditionalDiagnosis: (state, action) => {
            const id = action.payload;
            state.additionalDiagnoses = state.additionalDiagnoses.filter(diag => diag.id !== id);
        },
        clearAdditionalDiagnoses: (state) => {
            state.additionalDiagnoses = [];
        },
        loadPatientDiagnoses: (state, action) => {
            const diagnoses = action.payload;
            if (!diagnoses || diagnoses.length === 0) {
                state.consultationDetails.FinalDiagnosis = "";
                state.consultationDetails.FinalDiagnosisId = null;
                state.consultationDetails.FinalDiagnosisNotes = "";
                state.additionalDiagnoses = [];
                return;
            }
            const primaryDiagnosis = diagnoses.find(d => d.IsPrimary) || diagnoses[0];
            const additionalDiagnoses = diagnoses.filter(d => !d.IsPrimary);
            if (primaryDiagnosis) {
                state.consultationDetails.FinalDiagnosis = primaryDiagnosis.diag?.Name || primaryDiagnosis.DiagnosisName || "";
                state.consultationDetails.FinalDiagnosisId = primaryDiagnosis.DiagnosisId;
                state.consultationDetails.FinalDiagnosisNotes = primaryDiagnosis.diag?.Desc || "";
            }
            state.additionalDiagnoses = additionalDiagnoses.map((diag, index) => ({
                id: index + 1,
                value: diag.diag?.Name || diag.DiagnosisName || "",
                notes: diag.diag?.Desc || "",
                diagnosisId: diag.DiagnosisId
            }));
        },
        setPreparedDiagnoses: (state, action) => {
            state.preparedDiagnoses = action.payload;
        },
        prepareAndStoreDiagnoses: (state) => {
            const diagnoses = [];
            if (state.consultationDetails.FinalDiagnosis && state.consultationDetails.FinalDiagnosis.trim() !== '') {
                const diagnosisFromMaster = state.diagnosisMaster.find(d => d.Id === state.consultationDetails.FinalDiagnosisId);
                const primaryDiagnosis = {
                    ConsultId: state.consultationId,
                    DiagnosisId: state.consultationDetails.FinalDiagnosisId,
                    diag: diagnosisFromMaster || {
                        Id: state.consultationDetails.FinalDiagnosisId,
                        Name: state.consultationDetails.FinalDiagnosis,
                        Desc: state.consultationDetails.FinalDiagnosisNotes || ""
                    }
                };
                diagnoses.push(primaryDiagnosis);
            }
            state.additionalDiagnoses.forEach(diag => {
                if (diag.value && diag.value.trim() !== '') {
                    const diagnosisFromMaster = state.diagnosisMaster.find(d => d.Id === diag.diagnosisId);
                    const additionalDiagnosis = {
                        ConsultId: state.consultationId,
                        DiagnosisId: diag.diagnosisId,
                        diag: diagnosisFromMaster || {
                            Id: diag.diagnosisId,
                            Name: diag.value,
                            Desc: diag.notes || ""
                        }
                    };
                    diagnoses.push(additionalDiagnosis);
                }
            });
            state.preparedDiagnoses = diagnoses;
        },
        resetOutpatientState: (state) => {
            return {
                ...initialState,
                consultationType: state.consultationType,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConsultationById.fulfilled, (state, action) => {
                const data = action.payload;
                console.log(data, 'gggggg');
                
                // 1. Consultation Details - Use Comorbidity from API
                state.consultationDetails = {
                    ChiefComplaint: data.ChiefComplaint || "",
                    HPI: data.HPI || "",
                    Comorbidities: data.Comorbidity || "",
                    SystemicExamination: data.PhysicalExamination || "",
                    FinalDiagnosis: data.FinalDiagnosis || "",
                    FinalDiagnosisNotes: data.FinalDiagnosisNotes || "",
                    FinalDiagnosisId: null,
                };
                
                state.prescribingDoctor = data.DoctorName || data.Doctor || '';
                
                // 2. Patient Details
                state.patientDetails = {
                    name: data.PatientName || "",
                    age: data.Age || "",
                    gender: data.Gender || "",
                    dob: data.Dob || "",
                    mrn: data.MRN || "",
                    displayId: data.DisplayId || "",
                    firstName: data.FirstName || "",
                    lastName: data.LastName || "",
                    phone: data.Phone || data.PhoneNumber || "",
                    email: data.Email || "",
                    patientId: data.Id || "",
                };
                
                // 3. Lab Test Details
                state.labTestDetails = {
                    Services: Array.isArray(data.Services) ? data.Services.join(',') : data.Services || "",
                    Packages: Array.isArray(data.Packages) ? data.Packages.join(',') : data.Packages || "",
                };
                
                // Prefill selected packages
                if (data.Packages && Array.isArray(data.Packages)) {
                    state.selectedPackages = state.packagesMaster.filter(pkg => 
                        data.Packages.includes(String(pkg.id))
                    );
                } else {
                    state.selectedPackages = [];
                }
                
                // Prefill selected lab services
                let serviceIds = [];
                if (data.PatientServiceList && Array.isArray(data.PatientServiceList) && data.PatientServiceList.length > 0) {
                    serviceIds = data.PatientServiceList.map(item => String(item.ServiceId));
                    state.patientServiceList = data.PatientServiceList;
                }
                if (data.Services && Array.isArray(data.Services)) {
                    serviceIds = [...serviceIds, ...data.Services.map(String)];
                }
                serviceIds = [...new Set(serviceIds)];
                
                state.selectedServices = state.labTestServices.filter(service => 
                    serviceIds.includes(String(service.id))
                );
                
                if (state.selectedServices.length === 0 && data.PatientServiceList && data.PatientServiceList.length > 0) {
                    state.selectedServices = data.PatientServiceList.map(item => ({
                        id: item.ServiceId,
                        serviceName: item.ServiceName,
                        isFromPatientServiceList: true,
                        _original: item
                    }));
                }
                
                // 4. Imaging Services
                let imagingServiceIds = [];
                if (data.PatientServiceList && Array.isArray(data.PatientServiceList) && data.PatientServiceList.length > 0) {
                    imagingServiceIds = data.PatientServiceList
                        .filter(item => {
                            if (item.ServiceGroupId === IMAGING_GROUP_ID) return true;
                            const imagingKeywords = ['x-ray', 'mri', 'ct', 'ultrasound', 'radiology', 'imaging', 'scan'];
                            if (item.ServiceName && imagingKeywords.some(keyword => 
                                item.ServiceName.toLowerCase().includes(keyword)
                            )) return true;
                            return false;
                        })
                        .map(item => String(item.ServiceId));
                }
                if (data.ImagingServices && Array.isArray(data.ImagingServices)) {
                    imagingServiceIds = [...imagingServiceIds, ...data.ImagingServices.map(String)];
                }
                imagingServiceIds = [...new Set(imagingServiceIds)];
                
                if (imagingServiceIds.length > 0) {
                    state.selectedImagingServices = state.imagingServicesMaster.filter(s =>
                        imagingServiceIds.includes(String(s.id))
                    );
                    if (state.selectedImagingServices.length === 0 && data.PatientServiceList) {
                        state.selectedImagingServices = data.PatientServiceList
                            .filter(item => {
                                if (item.ServiceGroupId === IMAGING_GROUP_ID) return true;
                                const imagingKeywords = ['x-ray', 'mri', 'ct', 'ultrasound', 'radiology', 'imaging', 'scan'];
                                if (item.ServiceName && imagingKeywords.some(keyword => 
                                    item.ServiceName.toLowerCase().includes(keyword)
                                )) return true;
                                return false;
                            })
                            .map(item => ({
                                id: item.ServiceId,
                                serviceName: item.ServiceName,
                                isFromPatientServiceList: true
                            }));
                    }
                }
                
                // 5. Lab Test Description
                if (data.ServicePrescriptionComment) {
                    state.labTestDescription = data.ServicePrescriptionComment;
                }
                
                // 6. Imaging Description
                if (data.ImagingPrescriptionComment) {
                    state.imagingDescription = data.ImagingPrescriptionComment;
                }
                
                // 7. Department
                state.department = {
                    deptId: data.DepartmentId || "",
                    deptname: data.DepartmentName || ""
                };
                
                state.consultationId = data.Id;
                state.consultationData = data;
                
                if (data.ConsultationType) {
                    state.consultationType = data.ConsultationType;
                }
                
                // 8. Vital Signs
                if (data.Vitals) {
                    state.vitalSigns = {
                        bpSystolic: data.Vitals.BloodPressureSystolic ?? data.Vitals.BpSystolic ?? '',
                        bpDiastolic: data.Vitals.BloodPressureDiastolic ?? data.Vitals.BpDiastolic ?? '',
                        heartRate: data.Vitals.HeartRate ?? data.Vitals.Pulse ?? '',
                        respiratoryRate: data.Vitals.RespiratoryRate ?? data.Vitals.Respiration ?? '',
                        temperature: data.Vitals.Temperature ?? '',
                        oxygenSaturation: data.Vitals.OxygenSaturation ?? data.Vitals.SpO2 ?? '',
                        weight: data.Vitals.Weight ?? null,
                        height: data.Vitals.Height ?? null,
                        bmi: data.Vitals.BMI ?? null,
                        bodySurfaceArea: data.Vitals.BodySurfaceArea ?? null
                    };
                    const w = state.vitalSigns.weight;
                    const h = state.vitalSigns.height;
                    if (w && h && w > 0 && h > 0) {
                        const heightM = h / 100;
                        if (!state.vitalSigns.bmi) {
                            state.vitalSigns.bmi = parseFloat((w / (heightM * heightM)).toFixed(1));
                        }
                        if (!state.vitalSigns.bodySurfaceArea) {
                            state.vitalSigns.bodySurfaceArea = parseFloat(Math.sqrt((h * w) / 3600).toFixed(2));
                        }
                    }
                }
                
                // 9. Medicines
                if (data.Prescriptions && Array.isArray(data.Prescriptions)) {
                    state.prescribedMedicines = data.Prescriptions.map(prescription => ({
                        MedicineId: prescription.MedicineId || 0,
                        MedicineName: prescription.MedicineName || '',
                        Dosage: prescription.Dosage || '',
                        PrescriptionForm: prescription.PrescriptionForm || '',
                        PrescriptionTime: prescription.PrescriptionTime || [],
                        StartDate: prescription.StartDate || null,
                        EndDate: prescription.EndDate || null,
                        Instructions: prescription.Instructions || '',
                        TotalPrescribed: prescription.TotalPrescribed || 0,
                        RequiresRefill: prescription.RequiresRefill || false,
                        MaxRefills: prescription.MaxRefills || 0,
                        TotalPills: prescription.TotalPills || 0,
                        BeforeFood: prescription.BeforeFood || false
                    }));
                }
                
                // 10. Referral Information
                if (data.ReferralDoctorId) {
                    state.referralDoctorId = data.ReferralDoctorId;
                    state.referralNotes = data.ReferralNotes || '';
                    state.referralStatus = data.ReferralStatus || 'Pending';
                    state.selectedReferralDoctor = {
                        Id: data.ReferralDoctorId,
                        Name: data.ReferralDoctorName || '',
                        DepartmentId: data.ReferralDepartmentId || 0,
                        DepartmentName: data.ReferralDepartmentName || ''
                    };
                }
                
                // 11. Patient Journal
                if (data.PatientJournal) {
                    state.patientJournal = data.PatientJournal;
                }
                
                // ========== FIX: Specialty Info Prefilling - Updated for multiple templates ==========
                // 12. Specialty Info from API - Prefill templates and their values
                if (data.SpecialtyInfo && Array.isArray(data.SpecialtyInfo) && data.SpecialtyInfo.length > 0) {
                    // Clear existing specialty info
                    state.specialtyInfo = [];
                    
                    // Process each template's specialty info
                    data.SpecialtyInfo.forEach(templateInfo => {
                        // Find the template in specialtyInfomaster to get full template details
                        const templateMaster = state.specialtyInfomaster.find(t => t.Id === templateInfo.TemplateId);
                        
                        if (templateMaster) {
                            // Add the template to selectedTemplates if not already present
                            const isTemplateSelected = state.selectedTemplates.some(t => t.Id === templateMaster.Id);
                            if (!isTemplateSelected) {
                                state.selectedTemplates.push({
                                    ...templateMaster,
                                    TemplateData: templateMaster.TemplateData || []
                                });
                            }
                        }
                        
                        // Build the specialty info structure for this template
                        const specialtyInfos = [];
                        
                        if (templateInfo.SpecialtyInfos && Array.isArray(templateInfo.SpecialtyInfos)) {
                            templateInfo.SpecialtyInfos.forEach(field => {
                                specialtyInfos.push({
                                    FieldName: field.FieldName,
                                    FieldType: field.FieldType || 'Ordinary',
                                    Value: field.Value || ''
                                });
                            });
                        }
                        
                        // Add to specialtyInfo state
                        state.specialtyInfo.push({
                            TemplateId: templateInfo.TemplateId,
                            SpecialtyInfos: specialtyInfos
                        });
                    });
                }
                
                // Handle the case where SpecialtyInfo is in a different format (single object)
                if (data.SpecialtyInfo && typeof data.SpecialtyInfo === 'object' && !Array.isArray(data.SpecialtyInfo) && data.SpecialtyInfo.TemplateId) {
                    // If it's a single object, convert to array
                    const specialtyArray = [data.SpecialtyInfo];
                    
                    specialtyArray.forEach(templateInfo => {
                        const templateMaster = state.specialtyInfomaster.find(t => t.Id === templateInfo.TemplateId);
                        if (templateMaster) {
                            const isTemplateSelected = state.selectedTemplates.some(t => t.Id === templateMaster.Id);
                            if (!isTemplateSelected) {
                                state.selectedTemplates.push({
                                    ...templateMaster,
                                    TemplateData: templateMaster.TemplateData || []
                                });
                            }
                        }
                        
                        const specialtyInfos = [];
                        if (templateInfo.SpecialtyInfos && Array.isArray(templateInfo.SpecialtyInfos)) {
                            templateInfo.SpecialtyInfos.forEach(field => {
                                specialtyInfos.push({
                                    FieldName: field.FieldName,
                                    FieldType: field.FieldType || 'Ordinary',
                                    Value: field.Value || ''
                                });
                            });
                        }
                        
                        state.specialtyInfo.push({
                            TemplateId: templateInfo.TemplateId,
                            SpecialtyInfos: specialtyInfos
                        });
                    });
                }
                // ========== END Specialty Info Prefilling ==========
            })
            .addCase(fetchConsultationById.rejected, (state, action) => {
                console.error("Failed to fetch consultation:", action.payload);
            })
            .addCase(fetchConsultationImages.fulfilled, (state, action) => {
                const imagePaths = action.payload || [];
                state.images = imagePaths.map((path, index) => ({
                    url: path.startsWith('http') ? path : `https://laaficonsultationdev.azurewebsites.net${path}`,
                    preview: path.startsWith('http') ? path : `https://laaficonsultationdev.azurewebsites.net${path}`,
                    name: path.split('/').pop() || `image-${index}`,
                    isExisting: true
                }));
            })
            .addCase(fetchConsultationImages.rejected, (state, action) => {
                console.error("Failed to fetch consultation images:", action.payload);
            })
            .addCase(fetchMedicineMaster.fulfilled, (state, action) => {
                state.medicinemaster = action.payload;
            })
            .addCase(fetchMedicineMaster.rejected, (state, action) => {
                console.error("Failed to fetch medicine master:", action.payload);
                state.medicinemaster = [];
            })
            .addCase(addNewMedicineToMaster.fulfilled, (state, action) => { })
            .addCase(addNewMedicineToMaster.rejected, (state, action) => {
                console.error("Failed to add new medicine:", action.payload);
            })
            .addCase(fetchSpecialtyInfoMaster.fulfilled, (state, action) => {
                state.specialtyInfomaster = action.payload;
            })
            .addCase(fetchSpecialtyInfoMaster.rejected, (state, action) => {
                console.error("Failed to fetch specialty info master:", action.payload);
            })
            .addCase(fetchOptometristTemplates.fulfilled, (state, action) => {
                state.specialtyInfomaster = action.payload;
            })
            .addCase(fetchOptometristTemplates.rejected, (state, action) => {
                console.error("Failed to fetch optometrist templates:", action.payload);
                state.specialtyInfomaster = [];
            })
            .addCase(fetchAllDiagnosis.fulfilled, (state, action) => {
                state.diagnosisMaster = action.payload;
            })
            .addCase(fetchAllDiagnosis.rejected, (state, action) => {
                console.error("Diagnosis Master fetch failed:", action.payload);
                state.diagnosisMaster = [];
            })
            .addCase(fetchPatientDiagnoses.fulfilled, (state, action) => {
                const diagnoses = action.payload;
                if (diagnoses && diagnoses.length > 0) {
                    outpatientSlice.caseReducers.loadPatientDiagnoses(state, {
                        payload: diagnoses,
                        type: 'outpatient/loadPatientDiagnoses'
                    });
                }
            })
            .addCase(fetchPatientDiagnoses.rejected, (state, action) => {
                console.error("Patient diagnoses fetch failed:", action.payload);
            })
            .addCase(savePatientDiagnoses.fulfilled, (state, action) => { })
            .addCase(savePatientDiagnoses.rejected, (state, action) => {
                console.error("Patient diagnoses save failed:", action.payload);
            })
            .addCase(registerNewDiagnosis.fulfilled, (state) => { })
            .addCase(registerNewDiagnosis.rejected, (state, action) => {
                console.error("Diagnosis registration failed:", action.payload);
            })
            .addCase(fetchServiceGroups.fulfilled, (state, action) => {
                state.serviceGroups = action.payload;
            })
            .addCase(fetchServiceGroups.rejected, (state, action) => {
                console.error("Service groups fetch failed:", action.payload);
            })
            .addCase(fetchLabTestServices.fulfilled, (state, action) => {
                const allServices = action.payload;
                state.labTestServices = allServices.filter(s => s.groupId !== IMAGING_GROUP_ID);
                state.imagingServicesMaster = allServices.filter(s => s.groupId === IMAGING_GROUP_ID);
                
                // After labTestServices are loaded, re-match selected services
                const serviceIds = state.selectedServices
                    .filter(s => s.id && !s.isFromPatientServiceList)
                    .map(s => String(s.id));
                
                if (serviceIds.length > 0) {
                    const matchedServices = state.labTestServices.filter(s => 
                        serviceIds.includes(String(s.id))
                    );
                    if (matchedServices.length > 0) {
                        state.selectedServices = matchedServices;
                    }
                }
                
                // Re-match imaging services
                const imagingIds = state.selectedImagingServices
                    .filter(s => s.id && !s.isFromPatientServiceList)
                    .map(s => String(s.id));
                
                if (imagingIds.length > 0) {
                    const matchedImaging = state.imagingServicesMaster.filter(s => 
                        imagingIds.includes(String(s.id))
                    );
                    if (matchedImaging.length > 0) {
                        state.selectedImagingServices = matchedImaging;
                    }
                }
            })
            .addCase(fetchLabTestServices.rejected, (state, action) => {
                console.error("Lab test services fetch failed:", action.payload);
                state.labTestServices = [];
                state.imagingServicesMaster = [];
            })
            .addCase(fetchPackages.fulfilled, (state, action) => {
                state.packagesMaster = action.payload;
                
                // After packages are loaded, re-match selected packages
                const packageIds = state.selectedPackages
                    .filter(p => p.id)
                    .map(p => String(p.id));
                
                if (packageIds.length > 0) {
                    const matchedPackages = state.packagesMaster.filter(p => 
                        packageIds.includes(String(p.id))
                    );
                    if (matchedPackages.length > 0) {
                        state.selectedPackages = matchedPackages;
                    }
                }
            })
            .addCase(fetchPackages.rejected, (state, action) => {
                console.error("Packages fetch failed:", action.payload);
            })
            .addCase(getPatientConsultations.fulfilled, (state, action) => {
                state.loading = false;
                state.pastConsultations = action.payload?.Consultations || [];
            })
            .addCase(fetchPatientConsultations.fulfilled, (state, action) => {
                state.loading = false;
                state.patientConsultations = action.payload?.Consultations || action.payload || [];
            })
            .addCase(fetchPatientConsultations.rejected, (state, action) => {
                state.loading = false;
                state.patientConsultations = [];
                console.error("Patient consultations fetch failed:", action.payload);
            })
            .addCase(fetchLaafiRecommendations.fulfilled, (state, action) => {
                state.laafiRecommendations = action.payload.Message;
            })
            .addCase(aiDiagnosisPost.fulfilled, (state, action) => {
                state.aiSuggestion = action.payload;
                state.aiError = null;
            });
    },
});

// --- Export Actions and Reducer ---
export const {
    setActiveComponent,
    setConsultationType,
    updateConsultationDetails,
    updatePatientDetails,
    updateMedicineDetails,
    removeMedicine,
    clearPrescribedMedicines,
    setAiSuggestion,
    clearAiSuggestion,
    updateLabTestDetails,
    setLabTestServices,
    clearLabTestServices,
    addSelectedPackage,
    removeSelectedPackage,
    addSelectedService,
    removeSelectedService,
    addSelectedImagingService,
    removeSelectedImagingService,
    clearSelectedImagingServices,
    addImages,
    removeImage,
    clearImages,
    setSelectedTemplates,
    updateSpecialtyInfo,
    updateReviewComplete,
    addSelectedTemplate,
    removeSelectedTemplate,
    setPreparedDiagnoses,
    prepareAndStoreDiagnoses,
    updateMainDiagnosis,
    addAdditionalDiagnosis,
    updateAdditionalDiagnosis,
    removeAdditionalDiagnosis,
    clearAdditionalDiagnoses,
    loadPatientDiagnoses,
    updateVitalSigns,
    updateReferralDoctor,
    updateWeight,
    updateHeight,
    updateLabTestDescription,
    resetOutpatientState,
    updateImagingDescription,
    updateMedicineAtIndex,
    updateReferralNotes,
    updateReferralStatus,
    updateSelectedReferralDoctor
} = outpatientSlice.actions;

export default outpatientSlice.reducer;